/**
 * Support workspace (FOR-350, owner only): our products' support inbox.
 * Types and pure helpers only, no React, so tests/support.test.mjs can pin
 * them. Shapes follow SupportAdminController in the API repo
 * (docs/support-api.md there is the product contract).
 */

export type SupportFolder = "inbox" | "waiting" | "all" | "closed" | "spam" | "test"
export type SupportKind = "contact" | "support" | "feedback"
export type SupportStatus = "open" | "waiting_on_team" | "waiting_on_customer" | "resolved" | "closed"
export type SupportCategory = "bug" | "idea" | "question" | "other"
export type SupportMessageType = "customer" | "team" | "system" | "note"
/** SupportMailer: none (nothing to send), sent, failed, held (sandbox), test (a test conversation). */
export type SupportEmailStatus = "none" | "sent" | "failed" | "held" | "test"
export type SupportMode = "sandbox" | "live"
export type SupportBlockType = "email" | "domain" | "ip"
export type ContextTone = "default" | "success" | "warning" | "danger"

export interface SupportProjectSettings {
  uploads: boolean
  max_upload_mb: number
  feedback_categories: string[]
  alert_on_bug: boolean
  include_in_daily_summary: boolean
  sandbox_alerts: boolean
  /** The product's square icon (https), shown in the nav, rows and the widget. */
  logo_url?: string | null
}

export interface SupportProject {
  id: number
  slug: string
  name: string
  persona_name: string | null
  brand_color: string | null
  mode: SupportMode
  sending_domain: string | null
  from_local: string
  inbound_domain: string | null
  hosted_domain: string | null
  app_url: string | null
  allowed_origins: string[] | null
  test_recipients: string[] | null
  context_url: string | null
  webhook_url: string | null
  feedback_board_id: number | null
  server_key_prefix: string | null
  /** The project's own Resend key is set (write-only: the API never returns it). */
  has_resend_key?: boolean
  settings: SupportProjectSettings
  /** `"Hannah at Casewell" <support@mail.trycasewell.com>`, null without a sending domain. */
  from: string | null
  live_at: string | null
  waiting_on_team: number
  unread: number
}

export type SupportProjectInput = Partial<
  Pick<
    SupportProject,
    | "name" | "persona_name" | "brand_color" | "sending_domain" | "from_local" | "inbound_domain" | "hosted_domain"
    | "app_url" | "allowed_origins" | "test_recipients" | "context_url" | "webhook_url" | "feedback_board_id"
  >
> & { settings?: Partial<SupportProjectSettings> }

export interface SupportProjectCreated {
  data: SupportProject
  identity_secret: string
  server_key: string
}

export type SupportSecret = "identity_secret" | "server_key"

export interface SupportContact {
  /** The product's own user id; null for an anonymous (contact form, email) person. */
  id: string | null
  email: string | null
  name: string | null
  verified: boolean
}

export interface SupportConversationRow {
  /** Public id, cv_… */
  id: string
  number: number
  /** Project slug. */
  project: string | null
  project_name: string | null
  brand_color: string | null
  kind: SupportKind
  status: SupportStatus
  subject: string | null
  category: SupportCategory | null
  external_key: string | null
  org: { id: string | null; name: string | null } | null
  contact: SupportContact | null
  channel: string | null
  /** What a team reply does now (API SupportMailer::delivery). */
  reply_delivery?: ReplyDelivery | null
  /** How it arrived, which says how the customer gets replies (viaOf() if absent). */
  via?: SupportVia
  created_at: string | null
  updated_at: string | null
  last_message_at: string | null
  test: boolean
  spam: boolean
  alert: boolean
  unread: boolean
  assignee_id: number | null
  assignee?: { id: number; name: string | null } | null
  task_id: number | null
  satisfaction: string | number | null
  first_response_at: string | null
  /** When the customer started waiting on us (status waiting_on_team), for the "waiting 3h" badge. */
  waiting_since?: string | null
  url: string
  preview: { type: SupportMessageType; text: string; author: string | null } | null
}

export interface SupportAttachment {
  id: string
  name: string
  type: string
  size: number
  /** How a viewer can show it; null = download only. Older API rows omit it (derive from type). */
  preview?: SupportPreviewKind | null
  /** An image thumbnail exists (?variant=thumb). */
  thumb?: boolean
  /** A direct-upload video: played from a short-lived storage link (?link=1). */
  direct?: boolean
}

export type SupportPreviewKind = "image" | "video" | "pdf" | "text"

/** The preview kind, from the API when it says, else from the type (rows from before previews). */
export function previewKind(a: Pick<SupportAttachment, "preview" | "type">): SupportPreviewKind | null {
  if (a.preview !== undefined) return a.preview ?? null
  if (["image/png", "image/jpeg", "image/webp", "image/gif"].includes(a.type)) return "image"
  if (["video/mp4", "video/webm", "video/quicktime"].includes(a.type)) return "video"
  if (a.type === "application/pdf") return "pdf"
  if (a.type === "text/plain" || a.type === "text/csv") return "text"
  return null
}

export interface SupportMessage {
  id: string
  type: SupportMessageType
  body: string
  author: { name: string | null; kind: "customer" | "team" | "system" }
  attachments: SupportAttachment[]
  created_at?: string
  /** Absent when the message never had an email state. */
  email_status?: SupportEmailStatus
  email_preview?: string | null
}

export interface SupportEvent {
  event: string
  data: Record<string, unknown> | null
  created_at: string
}

export interface SupportConversation extends SupportConversationRow {
  ctx: Record<string, unknown> | null
  source_url: string | null
  messages: SupportMessage[]
  events: SupportEvent[]
  context_available: boolean
  /** Other conversations from the same person or org in this project, newest first. */
  history?: SupportHistoryItem[]
}

export interface SupportHistoryItem {
  id: string
  number: number
  subject: string | null
  status: SupportStatus
  kind: SupportKind
  spam: boolean
  same_person: boolean
  created_at: string | null
}

export interface SupportSnippet {
  id: number
  /** Project slug, or null for every project. */
  project: string | null
  title: string
  body: string
  uses: number
  updated_at: string | null
}

export interface SupportOutbox {
  id: number
  state: "pending" | "sending" | "sent" | "cancelled" | "failed"
  send_at: string | null
  message_id: number | null
}

export interface SupportStats {
  days: number
  conversations: number
  first_response_median_seconds: number | null
  first_responses: number
  resolve_median_seconds: number | null
  resolved: number
  satisfaction: { up: number; down: number; up_share: number | null }
  reopens: number
  waiting_on_team: number
  by_origin: Record<string, number>
  by_kind: Record<string, number>
}

/** Fill a snippet's placeholders for one conversation. */
export function fillSnippet(body: string, vars: { first_name?: string | null; persona?: string | null; product?: string | null }): string {
  return body
    .replace(/\{first_name\}/g, vars.first_name?.trim() || "there")
    .replace(/\{persona\}/g, vars.persona?.trim() || "The team")
    .replace(/\{product\}/g, vars.product?.trim() || "")
}

/** "45m", "3h", "2d": how long someone has waited. */
export function waitedFor(since: string | null | undefined, now: number = Date.now()): { label: string; hours: number } | null {
  if (!since) return null
  const ms = now - new Date(since).getTime()
  if (!Number.isFinite(ms) || ms < 0) return null
  const minutes = Math.floor(ms / 60_000)
  const hours = ms / 3_600_000
  const label = minutes < 60 ? `${Math.max(1, minutes)}m` : hours < 48 ? `${Math.floor(hours)}h` : `${Math.floor(hours / 24)}d`
  return { label, hours }
}

/** A list row's time, short: "now", "49m", "3h", "2d", then "Oct 20" (or "Oct 20, 2025" another year). */
export function shortAgo(value: string | null | undefined, now: number = Date.now()): string {
  if (!value) return ""
  const t = new Date(value).getTime()
  if (!Number.isFinite(t)) return ""
  const minutes = Math.floor((now - t) / 60_000)
  if (minutes < 1) return "now"
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 48) return `${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d`
  const d = new Date(t)
  const sameYear = d.getFullYear() === new Date(now).getFullYear()
  return d.toLocaleDateString("en-US", sameYear ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" })
}

/** "Org · Contact", with an org named like its contact (a one-person account) shown once. */
export function whoLine(org: string | null | undefined, contact: SupportContact | null): string {
  const person = contactLabel(contact).trim()
  const o = org?.trim()
  return o && o.toLowerCase() !== person.trim().toLowerCase() ? `${o} · ${person}` : person
}

/** Initials for an assignee chip: "Justin Gluska" → "JG", "Lori" → "LO". */
export function initials(name: string | null | undefined): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return "?"
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/** Seconds as "42m", "3.5h", "2.1d" for the stats page. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return "—"
  if (seconds < 3600) return `${Math.max(1, Math.round(seconds / 60))}m`
  if (seconds < 172_800) return `${(seconds / 3600).toFixed(seconds < 36_000 ? 1 : 0)}h`
  return `${(seconds / 86_400).toFixed(1)}d`
}

export interface SupportConversationList {
  data: SupportConversationRow[]
  current_page: number
  last_page: number
  total: number
}

export interface SupportCounts {
  /** Open conversations assigned to the caller. */
  mine?: number
  /** Open and unread, or waiting on our reply (spam and test excluded): the workspace switcher badge. */
  needs_action?: number
  unread: number
  waiting_on_team: number
  spam: number
}

export interface SupportContextRow {
  label: string
  value?: string | null
  url?: string | null
  tone?: ContextTone
}

export interface SupportContext {
  available: boolean
  error?: string
  title?: string | null
  subtitle?: string | null
  sections?: { title: string; rows: SupportContextRow[] }[]
  links?: { label: string; url: string }[]
}

export interface SupportBlock {
  id: number
  project_id: number | null
  type: SupportBlockType
  value: string
  reason: string | null
  created_at: string | null
}

export interface SupportListFilters {
  folder: SupportFolder
  project: string
  /** "me" for the Mine view; otherwise empty. */
  assignee: string
  via: string
  kind: string
  status: string
  q: string
  page: number
}

/* ------------------------------------------------------------------------ */
/* Labels                                                                    */
/* ------------------------------------------------------------------------ */

export const FOLDERS: { value: SupportFolder; label: string }[] = [
  { value: "inbox", label: "Inbox" },
  { value: "waiting", label: "Waiting on us" },
  { value: "all", label: "All" },
  { value: "closed", label: "Closed" },
  { value: "spam", label: "Spam" },
  { value: "test", label: "Test" },
]

export const STATUS_LABEL: Record<SupportStatus, string> = {
  open: "Open",
  waiting_on_team: "Waiting on us",
  waiting_on_customer: "Waiting on customer",
  resolved: "Resolved",
  closed: "Closed",
}

export const STATUSES = Object.keys(STATUS_LABEL) as SupportStatus[]

/**
 * How a conversation arrived. It also says how the customer gets our replies,
 * so the inbox shows it on every row and in the header.
 */
export type SupportVia = "email" | "in_app" | "contact" | "api" | "import"

export const VIAS: SupportVia[] = ["email", "in_app", "contact", "api", "import"]

export const VIA_LABEL: Record<SupportVia, string> = {
  email: "Email",
  in_app: "In-app",
  contact: "Contact form",
  api: "Automatic",
  import: "Imported",
}

/** What the customer gets when we reply, for the tooltip and the header. */
export const VIA_REPLY: Record<SupportVia, string> = {
  email: "Replies go by email",
  in_app: "They read replies in the product; the email is only a notice",
  contact: "Not signed in, so the full reply goes by email",
  api: "Opened by the product's server; replies show in the product",
  import: "Imported from the product's old support system",
}

/** The API's `via`, or the same derivation from channel and kind (SupportConversation::via()). */
export function viaOf(row: Pick<SupportConversationRow, "via" | "channel" | "kind">): SupportVia {
  if (row.via && (VIAS as string[]).includes(row.via)) return row.via
  if (row.channel === "import") return "import"
  if (row.channel === "email") return "email"
  if (row.kind === "contact") return "contact"
  if (row.channel === "api" || row.channel === "automatic") return "api"
  return "in_app"
}

export const KIND_LABEL: Record<SupportKind, string> = {
  contact: "Contact",
  support: "Support",
  feedback: "Feedback",
}

export const KINDS = Object.keys(KIND_LABEL) as SupportKind[]

export const CATEGORY_LABEL: Record<SupportCategory, string> = {
  bug: "Bug",
  idea: "Idea",
  question: "Question",
  other: "Other",
}

type Tone = "success" | "info" | "warning" | "error" | "violet" | "neutral"

export function statusTone(status: SupportStatus): Tone {
  switch (status) {
    case "waiting_on_team":
      return "warning"
    case "open":
      return "info"
    case "waiting_on_customer":
      return "violet"
    case "resolved":
      return "success"
    default:
      return "neutral"
  }
}

export const EMAIL_STATUS: Record<SupportEmailStatus, { label: string; tone: Tone; title: string }> = {
  sent: { label: "Emailed", tone: "success", title: "Sent to the customer" },
  held: { label: "Held (sandbox)", tone: "warning", title: "The project is in sandbox, so the customer was not emailed" },
  test: { label: "Test, not emailed", tone: "violet", title: "A test conversation never emails the customer" },
  failed: { label: "Email failed", tone: "error", title: "Sending failed" },
  none: { label: "No email", tone: "neutral", title: "Nothing was emailed for this message" },
}

export const CONTEXT_TONE_CLASS: Record<ContextTone, string> = {
  default: "text-foreground",
  success: "text-[#00713fde] dark:text-[#46fea5d4]",
  warning: "text-[#ab6400] dark:text-[#ffca16]",
  danger: "text-[#c40006d3] dark:text-[#ff9592]",
}

/** The conversation's kind and category: "Feedback · Bug", or just "Contact". */
export function kindLabel(c: Pick<SupportConversationRow, "kind" | "category">): string {
  const kind = KIND_LABEL[c.kind] ?? c.kind
  return c.category ? `${kind} · ${CATEGORY_LABEL[c.category] ?? c.category}` : kind
}

/** Who wrote in: name, else email, else "Anonymous". */
export function contactLabel(contact: SupportContact | null): string {
  if (!contact) return "Anonymous"
  return contact.name || contact.email || "Anonymous"
}

/**
 * A contact the product did not vouch for: anonymous people (contact form,
 * email) are never verified, so anything they claim about themselves is
 * unproven.
 */
export function isUnverified(contact: SupportContact | null): boolean {
  return !!contact && !contact.verified
}

/**
 * What a team reply on this conversation does, as the API computes it
 * (SupportMailer::delivery, so per-project switches such as
 * signed_in_full_text are already applied): notification (signed in, a notice
 * linking back into the product), full_email (the reply text by email), held
 * (sandbox: kept, agency copies only) or none (no address, spam or a test).
 */
export type ReplyDelivery = "notification" | "full_email" | "held" | "none"

type ReplyTarget = {
  kind: SupportKind
  contact: SupportContact | null
  reply_delivery?: ReplyDelivery | null
  test?: boolean
  spam?: boolean
  project_mode?: SupportMode | null
}

/** The API's reply_delivery, or (an API without it) the old signed-in rule. */
export function replyDelivery(c: ReplyTarget): ReplyDelivery {
  if (c.reply_delivery) return c.reply_delivery
  if (!c.contact?.email || c.test || c.spam) return "none"
  if (c.project_mode && c.project_mode !== "live") return "held"
  return c.kind !== "contact" && c.contact.verified ? "notification" : "full_email"
}

/** The composer's "who and how" line, e.g. "Reply to Nikki <n@x.com> by email (full text)". */
export function replyLine(c: ReplyTarget, copies: string[] | null = null): string {
  const contact = c.contact
  const who = contact?.name && contact.email && contact.name !== contact.email ? `${contact.name} <${contact.email}>` : contact?.email || contact?.name || "Anonymous"
  switch (replyDelivery(c)) {
    case "notification":
      return `Reply to ${who} in-app, they get an email notification`
    case "full_email":
      return `Reply to ${who} by email (full text)`
    case "held":
      return `Reply to ${who}. Held in sandbox${copies?.length ? `, a copy goes to ${copies.join(", ")}` : ", nothing is sent"}`
    default:
      if (c.test) return `Reply to ${who}. Test conversation, nothing is sent`
      if (c.spam) return `Reply to ${who}. Marked spam, nothing is sent`
      return c.kind !== "contact" && contact?.verified
        ? `Reply to ${who} in-app, no email on file so no notification`
        : `Reply to ${who}, no email on file so nobody is notified`
  }
}

/** A https logo URL from project settings, or null (anything else is ignored). */
export function logoUrl(settings: Partial<SupportProjectSettings> | null | undefined): string | null {
  const url = settings?.logo_url
  return typeof url === "string" && /^https:\/\/[^\s"'<>]+$/i.test(url) ? url : null
}

/** The project's brand color, or a neutral gray when it has none (or a malformed one). */
export function brandColor(color: string | null | undefined): string {
  return color && /^#[0-9a-f]{6}$/i.test(color) ? color : "#8b8d98"
}

/** What Support accepts (the API refuses anything else): images and videos, matched by type or by name. */
export const ATTACH_ACCEPT = "image/png,image/jpeg,image/webp,image/gif,video/mp4,video/webm,video/quicktime,.png,.jpg,.jpeg,.webp,.gif,.mp4,.webm,.mov"
const ATTACH_TYPES = new Set(["image/png", "image/jpeg", "image/webp", "image/gif", "video/mp4", "video/webm", "video/quicktime"])

export function isAcceptedAttachment(f: { type: string; name: string }): boolean {
  return ATTACH_TYPES.has(f.type) || /\.(png|jpe?g|webp|gif|mp4|webm|mov)$/i.test(f.name)
}

export function formatBytes(size: number): string {
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

/** Event names from the ledger, as a sentence: "secret.rotated" → "Secret rotated". */
export function eventLabel(event: string): string {
  const known: Record<string, string> = {
    "conversation.created": "Conversation opened",
    "message.created": "Message added",
    "status.changed": "Status changed",
    "marked.spam": "Marked spam",
    "marked.not_spam": "Marked not spam",
    "task.created": "Task created",
    "secret.rotated": "Secret rotated",
    "mode.changed": "Mode changed",
  }
  if (known[event]) return known[event]
  const words = event.replace(/[._]+/g, " ").trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** A short "key: value" summary of an event's data, without ids that mean nothing to a reader. */
export function eventDetail(data: Record<string, unknown> | null): string {
  if (!data) return ""
  return Object.entries(data)
    .filter(([k, v]) => v !== null && v !== undefined && v !== "" && k !== "by" && !k.endsWith("_id"))
    .map(([k, v]) => `${k.replace(/_/g, " ")} ${typeof v === "object" ? JSON.stringify(v) : String(v)}`)
    .join(", ")
}

/** One line in a conversation's timeline, already worded for a reader. */
export interface TimelineItem {
  label: string
  detail: string
  /** The raw event(s), for the tooltip. */
  raw: string
  at: string
  tone: "default" | "success" | "warning" | "danger"
  /** Consecutive views folded into one line. */
  views?: number
}

const CHANNEL_WORDS: Record<string, string> = {
  widget: "in the app",
  hosted: "on the help page",
  email: "by email",
  api: "from the product",
  automatic: "automatically",
  import: "imported",
  inbox: "from the inbox",
}

const MESSAGE_WORDS: Record<string, string> = {
  customer: "Customer wrote",
  team: "Reply added",
  note: "Internal note added",
  system: "System message",
}

const EMAIL_WORDS: Record<string, { label: string; tone: TimelineItem["tone"] }> = {
  "email.sent": { label: "Reply sent by email", tone: "success" },
  "email.failed": { label: "Reply email failed", tone: "danger" },
  "email.held_sandbox": { label: "Email held (sandbox)", tone: "warning" },
  "email.skipped_test": { label: "Email skipped (test)", tone: "default" },
}

function rawOf(e: { event: string; data: Record<string, unknown> | null }): string {
  return e.data ? `${e.event} ${JSON.stringify(e.data)}` : e.event
}

function oneEvent(e: { event: string; data: Record<string, unknown> | null; created_at: string }): TimelineItem {
  const d = e.data ?? {}
  const str = (k: string) => (typeof d[k] === "string" ? (d[k] as string) : "")
  const base = { raw: rawOf(e), at: e.created_at, tone: "default" as TimelineItem["tone"], detail: "" }
  const status = (v: string) => STATUS_LABEL[v as SupportStatus] ?? v
  switch (e.event) {
    case "conversation.created":
      return { ...base, label: "Conversation opened", detail: [KIND_LABEL[str("kind") as SupportKind], CHANNEL_WORDS[str("channel")]].filter(Boolean).join(", ") }
    case "message.created":
      return { ...base, label: MESSAGE_WORDS[str("type")] ?? "Message added", detail: CHANNEL_WORDS[str("channel")] ?? "" }
    case "status.changed":
      return { ...base, label: `Status: ${status(str("to"))}`, detail: str("from") ? `was ${status(str("from")).toLowerCase()}` : "" }
    case "assigned":
      return { ...base, label: d.to ? "Assigned" : "Unassigned" }
    case "conversation.reopened":
      return { ...base, label: "Reopened by the customer" }
    case "task.created":
      return { ...base, label: "Task created" }
    case "feedback.shipped":
      return { ...base, label: "Task done, customer told", tone: "success" }
    case "alert.unanswered":
      return { ...base, label: "Alert: still unanswered", detail: d.minutes ? `after ${d.minutes} min` : "", tone: "danger" }
    case "outreach.started":
      return { ...base, label: "Started by the team", detail: d.one_way ? "one way" : "" }
    case "marked.spam":
      return { ...base, label: "Marked spam", tone: "warning" }
    case "marked.not_spam":
      return { ...base, label: "Marked not spam" }
    case "contact.detached":
      return { ...base, label: "Contact detached (account deleted)" }
  }
  if (EMAIL_WORDS[e.event]) return { ...base, ...EMAIL_WORDS[e.event] }
  return { ...base, label: eventLabel(e.event), detail: eventDetail(e.data) }
}

/**
 * A conversation's events as a readable timeline, newest first: views in a
 * row fold into one "Viewed N times" line, and a team reply followed by its
 * email (within a minute) reads as one line, e.g. "Reply sent by email".
 */
export function timelineItems(events: { event: string; data: Record<string, unknown> | null; created_at: string }[]): TimelineItem[] {
  const out: TimelineItem[] = []
  for (let i = 0; i < events.length; i++) {
    const e = events[i]
    if (e.event === "conversation.viewed") {
      const last = out[out.length - 1]
      if (last?.views) {
        last.views += 1
        last.at = e.created_at
        last.label = `Viewed ${last.views} times`
        continue
      }
      out.push({ label: "Viewed", detail: "", raw: "conversation.viewed", at: e.created_at, tone: "default", views: 1 })
      continue
    }
    const next = events[i + 1]
    if (
      e.event === "message.created" && e.data?.type === "team" && next && EMAIL_WORDS[next.event] &&
      Math.abs(new Date(next.created_at).getTime() - new Date(e.created_at).getTime()) <= 60_000
    ) {
      const email = EMAIL_WORDS[next.event]
      const label = next.event === "email.sent" ? "Reply sent by email" : next.event === "email.failed" ? "Reply added, email failed" : next.event === "email.held_sandbox" ? "Reply added, email held (sandbox)" : "Reply added, email skipped (test)"
      out.push({ label, detail: "", raw: `${rawOf(e)}\n${rawOf(next)}`, at: next.created_at, tone: email.tone })
      i += 1
      continue
    }
    out.push(oneEvent(e))
  }
  return out.reverse()
}

/** Filters from the query string; anything unknown falls back to the inbox defaults. */
export function filtersFromParams(params: URLSearchParams): SupportListFilters {
  const folder = params.get("folder")
  const kind = params.get("kind") ?? ""
  const status = params.get("status") ?? ""
  const page = Number(params.get("page") ?? "1")
  return {
    folder: FOLDERS.some((f) => f.value === folder) ? (folder as SupportFolder) : "inbox",
    project: params.get("project") ?? "",
    assignee: params.get("assignee") === "me" ? "me" : "",
    via: (VIAS as string[]).includes(params.get("via") ?? "") ? (params.get("via") as string) : "",
    kind: (KINDS as string[]).includes(kind) ? kind : "",
    status: (STATUSES as string[]).includes(status) ? status : "",
    q: params.get("q") ?? "",
    page: Number.isInteger(page) && page > 0 ? page : 1,
  }
}

/** The API query for a list: empty filters and the default folder are left out. */
export function listQuery(f: SupportListFilters): Record<string, string | number> {
  const out: Record<string, string | number> = { folder: f.folder }
  if (f.project) out.project = f.project
  if (f.assignee) out.assignee = f.assignee
  if (f.via) out.via = f.via
  if (f.kind) out.kind = f.kind
  if (f.status) out.status = f.status
  if (f.q.trim()) out.q = f.q.trim()
  if (f.page > 1) out.page = f.page
  return out
}

/** Test recipients must be agency addresses (the API refuses anything else). */
export function isAgencyAddress(email: string): boolean {
  return /^[^@\s]+@(gluska\.co|snowballrank\.com)$/i.test(email.trim())
}

/** An allowed origin is a scheme and host only, no path: https://app.example.com */
export function isOrigin(value: string): boolean {
  return /^https?:\/\/[^/\s]+$/.test(value.trim())
}

export function isDomain(value: string): boolean {
  return /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(value.trim())
}

/** One entry per line or comma, trimmed, without blanks or repeats. */
export function parseList(text: string): string[] {
  return [...new Set(text.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean))]
}
